import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Navbar from './components/Navbar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ApplicationForm from './pages/ApplicationForm';
import MyApplications from './pages/MyApplications';
import AdminDashboard from './pages/AdminDashboard';
import AdminApplications from './pages/AdminApplications';
import LeaveBalanceReport from './pages/LeaveBalanceReport';
import UserManagement from './pages/UserManagement';
import ApproverDashboard from './pages/ApproverDashboard';
import Settings from './pages/Settings';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import {
  parseLoginErrorParam,
  LOGIN_ERROR_STORAGE_KEY
} from './utils/loginErrorMessage';

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppContent />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

function LoginErrorCapture() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    if (error && location.pathname === '/login') {
      const message = parseLoginErrorParam(error);
      if (message) {
        try {
          sessionStorage.setItem(LOGIN_ERROR_STORAGE_KEY, message);
        } catch {
          /* ignore */
        }
      }
    }
  }, [location.pathname, location.search]);

  return null;
}

function AppMainRoutes({ user }) {
  return (
    <Routes>
      <Route path="/" element={user ? <Dashboard /> : <Navigate to="/login" />} />
      <Route path="/apply" element={user ? <ApplicationForm /> : <Navigate to="/login" />} />
      <Route path="/my-applications" element={user ? <MyApplications /> : <Navigate to="/login" />} />
      {user && user.is_approver && (
        <Route path="/approver" element={<ApproverDashboard />} />
      )}
      {user && user.is_admin && (
        <>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/applications" element={<AdminApplications />} />
          <Route path="/admin/leave-balance" element={<LeaveBalanceReport />} />
          <Route path="/admin/users" element={<UserManagement />} />
          <Route path="/admin/setting" element={<Settings />} />
        </>
      )}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  if (loading) {
    return (
      <>
        <LoginErrorCapture />
        <div className={`loading${isLoginPage ? ' loading--viewport' : ''}`}>
          <div className="spinner" />
        </div>
      </>
    );
  }

  return (
    <>
      <LoginErrorCapture />
      <Routes>
        <Route
          path="/login"
          element={!user ? <Login /> : <Navigate to="/" replace />}
        />
        <Route
          path="*"
          element={
            <>
              {user && <Navbar />}
              <main className="container mt-4">
                <AppMainRoutes user={user} />
              </main>
            </>
          }
        />
      </Routes>
    </>
  );
}

export default App;